import { useCallback, useEffect, useRef, useState } from 'react'
import { CaretDownOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import { useMediaQuery } from 'hooks/useMediaQuery'

import ScrollDownIcon from 'components/icons/ScrollDownIcon'

import { SelectOption } from '../type'

import '../ThumbnailCarousel/thumbnailCarousel.css'

interface SelectFloorProps {
  options: SelectOption[]
  onSelect: (value: number) => void
  value?: number
  placeholder?: string
  className?: string
  style?: React.CSSProperties
}

function SelectFloor({
  options,
  onSelect,
  value,
  placeholder,
  className,
  style
}: SelectFloorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedValue, setSelectedValue] = useState<number | undefined>(value)
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState<number>(0)
  const [showScrollIndicator, setShowScrollIndicator] = useState(false)

  const getTextFromDisplayLabel = (
    label: string | JSX.Element | undefined
  ): string => {
    if (!label) return ''
    if (typeof label === 'string') return label
    if (label.props && typeof label.props.children === 'string') {
      return label.props.children
    }
    return ''
  }
  const dropdownRef = useRef<HTMLDivElement>(null)
  const dropdownContentRef = useRef<HTMLDivElement>(null)
  const matches = useMediaQuery('(min-width: 768px)')
  const [showTooltip, setShowTooltip] = useState(false)
  const triggerHeight = 40 // px

  // Check scroll position and update indicator visibility
  const handleScroll = useCallback(() => {
    if (dropdownContentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        dropdownContentRef.current
      const isAtBottom = scrollHeight - (scrollTop + clientHeight) < 5 // 5px threshold
      const isScrollable = scrollHeight > clientHeight

      setShowScrollIndicator(isScrollable && !isAtBottom)
    }
  }, [])

  // Check if dropdown content is scrollable
  const checkScrollable = useCallback(() => {
    if (dropdownContentRef.current) {
      const { scrollHeight, clientHeight } = dropdownContentRef.current
      const isScrollable = scrollHeight > clientHeight
      setShowScrollIndicator(isScrollable)

      // Add or remove scroll event listener
      const element = dropdownContentRef.current
      if (isScrollable) {
        element.addEventListener('scroll', handleScroll)
      } else {
        element.removeEventListener('scroll', handleScroll)
      }

      return () => {
        element.removeEventListener('scroll', handleScroll)
      }
    }
  }, [handleScroll])

  useEffect(() => {
    if (isOpen) {
      // Small timeout to ensure the dropdown is rendered
      const timer = setTimeout(checkScrollable, 100)
      window.addEventListener('resize', checkScrollable)
      return () => {
        clearTimeout(timer)
        window.removeEventListener('resize', checkScrollable)
      }
    }
  }, [isOpen, options.length, checkScrollable])

  useEffect(() => {
    setSelectedValue(value)
  }, [value])

  useEffect(() => {
    const maxHeight = window.innerHeight * (matches ? 0.8 : 0.7)
    setDropdownMaxHeight(maxHeight - triggerHeight)
  }, [matches])

  const handleToggle = () => setIsOpen(!isOpen)

  const handleOptionClick = useCallback(
    (optionValue: number, e: React.MouseEvent) => {
      e.stopPropagation()
      if (selectedValue !== optionValue) {
        setSelectedValue(optionValue)
        onSelect(optionValue)
      }
      setIsOpen(false)
      setShowTooltip(false)
    },
    [onSelect, selectedValue]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, optionValue: number) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        if (selectedValue !== optionValue) {
          setSelectedValue(optionValue)
          onSelect(optionValue)
        }
        setIsOpen(false)
        setShowTooltip(false)
      }
    },
    [onSelect, selectedValue]
  )

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const selectedOption = options.find(
    (option) => option.value === selectedValue
  )
  const displayLabel = selectedOption ? selectedOption.label : placeholder

  return (
    <div
      className={`custom-select-wrapper relative ${className || ''}`}
      style={style}
      ref={dropdownRef}
    >
      <div
        className="flex justify-between items-center font-bold text-white h-[40px] px-2 cursor-pointer bg-[#5F9EA0] relative"
        onClick={handleToggle}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <Tooltip
          open={showTooltip}
          color="white"
          overlayInnerStyle={{
            backgroundColor: 'white',
            color: '#000000',
            fontWeight: 'bold',
            fontSize: 13
          }}
          autoAdjustOverflow
          mouseLeaveDelay={100}
          trigger={!matches ? 'click' : 'hover'}
          onOpenChange={(open) => {
            setShowTooltip(open)
            if (open) {
              setTimeout(() => setShowTooltip(false), 1000)
            }
          }}
          title={
            <span
              className="[&>*]:!text-[#373737]"
              style={{
                color: '#000000',
                fontWeight: 'bold',
                fontSize: 15
              }}
            >
              {displayLabel}
            </span>
          }
        >
          <div style={{ width: !matches ? '85%' : '90%' }}>
            {/* taiwind css truncate  "..." alignt top so use input replace */}
            <input
              type="text"
              readOnly
              value={getTextFromDisplayLabel(displayLabel)}
              style={{
                border: 'none',
                background: 'transparent',
                outline: 'none',
                pointerEvents: 'none',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                lineHeight: '1.2',
                padding: '2px 0',
                fontSize: matches ? '14px' : '12px',
                fontFamily: 'inherit',
                display: 'block',
                fontWeight: 'bold',
                width: '100%',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </Tooltip>

        <div className="absolute right-2">
          <CaretDownOutlined className="text-white" />
        </div>
      </div>

      {isOpen && (
        <div
          ref={dropdownContentRef}
          className="z-[999] overflow-y-auto bg-[#3EB1ADF2] text-white absolute w-full shadow-md"
          style={{
            maxHeight: `${dropdownMaxHeight}px`,
            scrollBehavior: 'smooth',
            position: 'relative',
            marginBottom: '32px'
          }}
        >
          <div className="relative">
            {options.map((option) => (
              <div
                key={option.value}
                role="button"
                tabIndex={0}
                className={`px-3 py-2 cursor-pointer hover:bg-[#FFFFFF52] ${
                  selectedValue === option.value
                    ? 'bg-[#FFFFFF52] font-semibold'
                    : ''
                }`}
                onClick={(e) => handleOptionClick(option.value, e)}
                onKeyDown={(e) => handleKeyDown(e, option.value)}
              >
                <Tooltip
                  color="white"
                  overlayInnerStyle={{
                    backgroundColor: 'white',
                    color: '#000000',
                    fontWeight: 'bold',
                    fontSize: 13
                  }}
                  title={
                    <span
                      className="[&>*]:!text-[#373737]"
                      style={{
                        color: '#000000',
                        fontWeight: 'bold',
                        fontSize: 15
                      }}
                    >
                      {option.label}
                    </span>
                  }
                >
                  <div style={{ width: '100%' }}>
                    {/* taiwind css truncate  "..." alignt top so use input replace */}
                    <input
                      type="text"
                      readOnly
                      value={getTextFromDisplayLabel(option.label)}
                      className="text-white w-full truncate"
                      style={{
                        border: 'none',
                        background: 'transparent',
                        outline: 'none',
                        pointerEvents: 'none',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        lineHeight: '1.2',
                        padding: '2px 0',
                        fontSize: matches ? '14px' : '12px',
                        fontFamily: 'inherit',
                        display: 'block',
                        fontWeight: 'normal',
                        width: '100%',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </Tooltip>
              </div>
            ))}
          </div>
          {/* Scroll indicator - positioned at bottom and hides when scrolled to bottom */}
          {showScrollIndicator && (
            <div
              className="sticky -bottom-1 left-0 right-0 h-5 flex items-center justify-center pointer-events-none"
              style={{
                background: '#3EB1ADF2',
                transition: 'opacity 0.2s ease-in-out',
                opacity: showScrollIndicator ? 1 : 0
              }}
            >
              <div
                style={{
                  background: '#FFFFFF52',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <ScrollDownIcon className="scale-50" />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

SelectFloor.defaultProps = {
  value: undefined,
  placeholder: '',
  className: '',
  style: undefined
}

export default SelectFloor
